import type { Article } from '../types';
import { h2, p, ul, ol, callout, table } from '../b';

/**
 * Pillar page for Search Console queries:
 * pay with telegram stars, telegram stars payment, stars payment,
 * stars payment telegram, telegram star payment, telegram stars payments,
 * telegram stars pay, starspayment, telegramstars, telegram stars.
 */
const article: Article = {
  slug: 'telegram-stars-payment',
  title: 'Telegram Stars Payment: How to Pay and Get Paid with Stars',
  metaTitle: 'Telegram Stars Payment | Pay & Get Paid with Stars',
  metaDescription:
    'What a Telegram Stars payment is, how to pay with Telegram Stars, and how to get a Stars payment as a creator. Create a payment link in minutes on telegram-stars.io.',
  keywords: [
    'telegram stars payment',
    'pay with telegram stars',
    'stars payment',
    'stars payment telegram',
    'telegram star payment',
    'telegram stars payments',
    'telegram stars pay',
    'starspayment',
    'telegramstars',
    'telegram stars',
  ],
  category: 'Guides',
  datePublished: '2026-10-15',
  dateModified: '2026-10-15',
  readingTimeMin: 7,
  excerpt:
    'A Telegram Stars payment is a checkout inside Telegram, priced in Stars (XTR). Buyers pay with Telegram Stars in two taps; sellers get a Stars payment link, a ledger and withdrawals to TON. This guide covers both sides.',
  keyTakeaways: [
    'Telegram Stars payment means paying or getting paid in Telegram’s in-app currency (XTR), not with a card form on a website.',
    'To pay with Telegram Stars: open a payment link or invoice, confirm in the Telegram sheet, buy Stars on the spot if your balance is low.',
    'To receive a Stars payment: create a no-code payment link on Telegram Stars Payment Platform, share it, track sales, withdraw to TON after 21 days.',
    '“Stars payment”, “pay with Telegram Stars” and “Telegram Stars pay” all point to the same flow: native Telegram checkout for digital goods.',
  ],
  body: [
    h2('What is a Telegram Stars payment?'),
    p(
      'A **Telegram Stars payment** is a purchase settled in Telegram Stars, the in-app currency with the code **XTR**. The buyer confirms inside Telegram’s native payment sheet. There is no external card form, no PayPal redirect and no crypto wallet popup. That is why people search for “stars payment”, “telegram star payment” or “telegramstars” when they want this flow.',
    ),
    p(
      'Stars are designed for **digital goods and services** on Telegram: premium channels, courses, templates, coaching, bot features, Mini App items and tips. Telegram requires Stars for many digital purchases on iOS and Android, so a Stars payment is often the only compliant way to sell inside the app.',
    ),

    h2('How to pay with Telegram Stars'),
    p(
      'If you want to **pay with Telegram Stars**, you only need a Telegram account and enough Stars in your balance. The seller sends you a payment link or an invoice in chat. Opening it shows the product title, description and the price in Stars.',
    ),
    ol([
      'Tap the **Telegram Stars payment** link (or the invoice button in a bot / Mini App).',
      'Review the amount in Stars and what you get.',
      'Confirm. Telegram debits your Stars balance.',
      'If you do not have enough Stars, Telegram offers to **buy Stars** (App Store, Google Play, @PremiumBot or Fragment), then completes the payment.',
    ]),
    p(
      'That is the whole “Telegram Stars pay” experience: two taps, no signup on a third-party checkout. For a deeper buyer guide, see [How to buy Telegram Stars](/blog/how-to-buy-telegram-stars).',
    ),

    h2('How to get paid: receive a Stars payment'),
    p(
      'Creators and merchants looking for **stars payment Telegram** usually want to *receive* money, not spend it. The fastest path is a shareable **Telegram Stars payment link** on [Telegram Stars Payment Platform](/):',
    ),
    ol([
      '[Sign in with Telegram](/login) — free, no monthly fee.',
      'Open **Invoices → New**, set a title, description and amount in Stars.',
      'Copy the payment link (or a short redirect like `telegram-stars.io/r/your-slug`).',
      'Share it in a channel, bio, DM or website button.',
      'Each **Telegram Stars payment** lands in your dashboard ledger in real time.',
    ]),
    callout(
      'Platform fee is a flat **3%**. Minimum withdrawal is **100 Stars** to a TON wallet after Telegram’s **21-day** hold. Details in [fees explained](/blog/telegram-stars-fees-explained) and [refunds & payout rules](/blog/telegram-stars-refunds-21-day-hold).',
    ),
    p(
      'Developers can also take Telegram Stars payments with the Bot API (`currency="XTR"`). See the [Bot API guide](/blog/telegram-stars-bot-api-developer-guide) and the no-code path in [How to accept Telegram Stars payments](/blog/how-to-accept-telegram-stars-payments).',
    ),

    h2('Stars payment vs card, PayPal and crypto'),
    table(
      ['', 'Telegram Stars payment', 'Card / PayPal', 'Crypto wallet'],
      [
        ['Where checkout happens', 'Inside Telegram', 'External website', 'Wallet app / web'],
        ['Best for on Telegram', 'Digital goods & bots', 'Physical goods, web shops', 'Web3 audiences'],
        ['Buyer friction', 'Very low (already in Telegram)', 'Higher (leave the app)', 'Medium / high'],
        ['Seller setup', 'Payment link in minutes', 'Merchant account + KYC', 'Wallet + invoices'],
        ['Payout', 'TON (via platform or Fragment)', 'Bank', 'Crypto'],
      ],
    ),
    p(
      'For selling *on Telegram*, a **Stars payment** usually converts better than sending people to Stripe or PayPal. Full comparison: [Telegram Stars vs Stripe, PayPal and crypto](/blog/telegram-stars-vs-stripe-paypal-crypto).',
    ),

    h2('Common searches, same product'),
    p(
      'Search and AI tools often use slightly different phrases for the same thing. On this site they all map to Telegram Stars Payment Platform:',
    ),
    ul([
      '**telegram stars payment** / **telegram stars payments** — accepting or making a payment in Stars',
      '**pay with telegram stars** / **telegram stars pay** — the buyer flow',
      '**stars payment** / **stars payment telegram** / **telegram star payment** — short forms of the same idea',
      '**starspayment** / **telegramstars** — compacted brand-style queries for Stars payment tools',
    ]),
    p(
      'If you landed here from any of those queries: create a free account, generate a payment link, and take your first Stars payment the same day.',
    ),

    h2('Start a Telegram Stars payment today'),
    p(
      'Open [telegram-stars.io](/), sign in with Telegram, create one invoice for your main offer, and pin the link where your audience already is. No bot to host. No card processor to configure. Just a Stars payment link and a live balance.',
    ),
  ],
  faq: [
    {
      q: 'What is a Telegram Stars payment?',
      a: 'A Telegram Stars payment is a purchase paid in Telegram Stars (currency code XTR) through Telegram’s native checkout. The buyer confirms in the app; the seller is credited in Stars and can later withdraw to TON.',
    },
    {
      q: 'How do I pay with Telegram Stars?',
      a: 'Open the seller’s payment link or invoice in Telegram, check the Stars amount, and confirm. If your Stars balance is too low, Telegram lets you buy Stars first, then completes the payment.',
    },
    {
      q: 'How do I accept a Stars payment on Telegram?',
      a: 'Create a payment link on Telegram Stars Payment Platform (telegram-stars.io), share it with customers, and track each Stars payment in the dashboard. You can also build your own bot with the Bot API and currency XTR.',
    },
    {
      q: 'Is “stars payment” the same as “Telegram Stars payment”?',
      a: 'Yes. “Stars payment”, “starspayment”, “telegram star payment” and “telegramstars” usually refer to paying or getting paid with Telegram Stars inside Telegram.',
    },
    {
      q: 'Can I get paid in Telegram Stars without coding?',
      a: 'Yes. Telegram Stars Payment Platform generates shareable payment links with no bot or server. Fee is 3%, minimum withdrawal 100 Stars to a TON wallet after the 21-day hold.',
    },
    {
      q: 'Where do Telegram Stars payments withdraw to?',
      a: 'Withdrawals are paid to a TON wallet. On the platform the minimum is 100 Stars; Telegram’s 21-day maturity applies before Stars become withdrawable.',
    },
  ],
  relatedSlugs: [
    'how-to-accept-telegram-stars-payments',
    'telegram-stars-payment-links',
    'how-to-buy-telegram-stars',
    'telegram-stars-vs-stripe-paypal-crypto',
  ],
};

export default article;
